/**
 * @file DocumentFilesSection.tsx
 * @description Блок «Файлы» карточки документа (phase19.31, часть 3): PDF
 * печатной формы — имя, размер, «Открыть»; если при выпуске PDF не создался —
 * янтарная строка и «Создать PDF» (право выпуска документов; у
 * аннулированного документа печатную форму не пересоздаём).
 *
 * У черновика файлов нет — блок не показываем (о PDF говорит план действий:
 * он создаётся при выпуске). Скачивание и печать — кнопками шапки, здесь
 * только просмотр файла, чтобы кнопки не повторялись.
 */

import { ExternalLink, FileText, RotateCw } from "lucide-react";

import { formatFileSize } from "./document-pdf";
import type { DocumentBusy } from "./useDocumentActions";
import { Button } from "../../ui/button";
import { LIST_FRAME_CLASS } from "../../ui/ListStates";
import { NoticeStrip } from "../../ui/NoticeStrip";
import { SectionCard } from "../../ui/SectionCard";
import type { DocumentDto } from "../../../lib/api-crm";

interface Props {
  doc: DocumentDto;
  canRegenerate: boolean;
  busy: DocumentBusy;
  onOpen: () => void;
  onRegenerate: () => void;
}

export function DocumentFilesSection({ doc, canRegenerate, busy, onOpen, onRegenerate }: Props) {
  // Черновик ещё без файла; аннулированному без файла создавать его незачем.
  if (doc.status === "DRAFT" || (doc.status === "VOIDED" && !doc.pdfStorageKey)) return null;

  const regenerate = canRegenerate ? (
    <Button variant="outline" onClick={onRegenerate} loading={busy === "regenerate"} disabled={busy !== null && busy !== "regenerate"}>
      <RotateCw className="size-4" aria-hidden />
      {doc.pdfStorageKey ? "Создать PDF заново" : "Создать PDF"}
    </Button>
  ) : null;

  if (!doc.pdfStorageKey) {
    return (
      <SectionCard title="Файлы">
        <NoticeStrip tone="warning" title="PDF не создан">
          <p>При выпуске печатная форма не собралась. {canRegenerate ? "Создайте PDF ещё раз." : "Попросите сотрудника с правом выпуска документов создать PDF."}</p>
          {regenerate ? <div className="mt-2">{regenerate}</div> : null}
        </NoticeStrip>
      </SectionCard>
    );
  }

  const size = formatFileSize(doc.pdfSizeBytes);
  return (
    <SectionCard title="Файлы">
      <div className={`${LIST_FRAME_CLASS} flex flex-wrap items-center gap-3 px-3 py-2`}>
        <FileText className="size-5 shrink-0 text-on-surface-variant" aria-hidden />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="type-body truncate font-medium text-on-surface">{doc.number}.pdf</span>
          <span className="type-caption text-on-surface-variant">{["Печатная форма", size].filter(Boolean).join(" · ")}</span>
        </span>
        <span className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={onOpen} loading={busy === "open"} disabled={busy !== null && busy !== "open"}>
            <ExternalLink className="size-4" aria-hidden />
            Открыть
          </Button>
          {regenerate}
        </span>
      </div>
    </SectionCard>
  );
}
